'use client';

import { HtmlRenderer } from '@eliadmin/core/src/components/ui/project-html-renderer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { TourTabHeading } from '../tour-content';

type FaqEntry = {
  question: string;
  answer: string;
};

const LIST_ITEM_REGEX = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
const PARAGRAPH_REGEX = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;

// WYSIWYG/Google Docs content wraps the question in styling tags, so allow any
// number of inline wrappers before the <strong>/<b> that marks the question.
const WRAPPER_TAGS = 'p|div|span|em|i|u|font|small';
const QUESTION_REGEX = new RegExp(
  `^\\s*(?:<(?:${WRAPPER_TAGS})\\b[^>]*>\\s*)*<(strong|b)\\b[^>]*>([\\s\\S]*?)<\\/\\1>`,
  'i',
);

// Leftovers between the question and the answer: whitespace, <br>, nbsp, the
// closing halves of the wrappers we skipped, and wrappers holding no text.
const ANSWER_PREFIX_REGEX = new RegExp(
  `^(?:\\s|&nbsp;|<br\\s*\\/?>|<\\/(?:${WRAPPER_TAGS}|strong|b)\\s*>|<(${WRAPPER_TAGS})\\b[^>]*>(?:\\s|&nbsp;|<br\\s*\\/?>)*<\\/\\1\\s*>)+`,
  'i',
);

function decodeEntities(value: string) {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');
}

function toPlainText(html: string) {
  return decodeEntities(html.replace(/<[^>]*>/g, '')).trim();
}

function stripAnswerPrefix(html: string) {
  let answer = html.trim();
  let previous = '';

  // Repeat so that nested empty wrappers unwind one layer per pass.
  while (answer !== previous) {
    previous = answer;
    answer = answer.replace(ANSWER_PREFIX_REGEX, '').trim();
  }

  return answer;
}

function matchAll(html: string, regex: RegExp) {
  const blocks: string[] = [];
  let match: RegExpExecArray | null;

  // regexes are module-level, reset before reuse
  regex.lastIndex = 0;

  while ((match = regex.exec(html)) !== null) {
    blocks.push(match[1]);
  }

  return blocks;
}

export function parseFaq(html: string): FaqEntry[] {
  if (!html || typeof html !== 'string') return [];

  const listItems = matchAll(html, LIST_ITEM_REGEX);
  const blocks = listItems.length > 0 ? listItems : matchAll(html, PARAGRAPH_REGEX);

  return blocks.reduce<FaqEntry[]>((entries, block) => {
    const questionMatch = block.match(QUESTION_REGEX);

    if (!questionMatch) return entries;

    const question = toPlainText(questionMatch[2]);

    if (!question) return entries;

    const answer = stripAnswerPrefix(block.slice(questionMatch[0].length));

    entries.push({ question, answer });

    return entries;
  }, []);
}

export function FaqTab({ title, html }: { title: string; html: string }) {
  const entries = parseFaq(html);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 lg:gap-8">
      <TourTabHeading title={title} />
      {entries.length > 0 ? (
        <Accordion type="single" collapsible className="w-full">
          {entries.map((entry, index) => (
            <AccordionItem key={`${entry.question}-${index}`} value={`faq-${index}`}>
              <AccordionTrigger className="gap-4 text-left text-base font-bold text-foreground transition-colors hover:no-underline data-[state=open]:text-primary sm:text-lg">
                {entry.question}
              </AccordionTrigger>
              <AccordionContent>
                <HtmlRenderer
                  html={entry.answer}
                  className={cn(
                    'ea-tour__description-tab-content w-full text-left text-sm font-light sm:text-base',
                  )}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <HtmlRenderer
          html={html}
          className={cn('ea-tour__description-tab-content w-full text-left')}
        />
      )}
    </div>
  );
}
